// Mirrors @agent-manager/sdk types that cross the API boundary (the SDK is a Node package).

export type ProviderId = 'openai' | 'anthropic' | 'google';
export type ReasoningEffort = 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export type AgentConfig = {
  model: { provider: ProviderId; name: string; reasoningEffort?: ReasoningEffort; maxTokens?: number; temperature?: number };
  system: string;
  userTemplate?: string;
  variables?: Array<{ name: string; required?: boolean; description?: string }>;
  output?: { type: 'text' } | { type: 'json_object' } | { type: 'json_schema'; name?: string; schema: Record<string, unknown>; strict?: boolean };
  tools?: string[];
  subAgents?: Array<{ slug: string; toolName: string; description: string }>;
  handoffs?: Array<{ slug: string; description?: string }>;
  maxTurns?: number;
  logPayloads?: boolean;
};

export type ConfigIssue = { level: 'error' | 'warning'; path: string; message: string };

export type ModelRow = {
  id: string;
  provider: ProviderId;
  name: string;
  label?: string;
  enabled: boolean;
  capabilities: { temperature: boolean; reasoningEfforts: ReasoningEffort[]; toolsRequireReasoningNone?: boolean; files: boolean; jsonSchema: boolean };
  pricing?: { input: number | null; output: number | null; cachedInput?: number | null };
};

export type AgentListRow = {
  id: string;
  slug: string;
  kind: 'agent' | 'flow';
  variables: Array<{ name: string; required?: boolean; description?: string }>;
  name: string;
  description: string;
  project: string;
  publishedVersionId: string | null;
  publishedVersion: number | null;
  canaryVersionId: string | null;
  canaryPercent: number;
  model: AgentConfig['model'] | null;
  hasDraft: boolean;
  archivedAt: string | null;
  updatedAt: string;
  stats24h: { requests: number; errors: number; costUsd: number | null; p95Ms: number | null };
};

export type Version = {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  changelog: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  publishedBy: string | null;
  config: AgentConfig | FlowConfig;
};

export type AgentDetail = {
  agent: AgentListRow & { projectId: string; createdBy: string | null; createdAt: string };
  versions: Version[];
};

export type TraceRow = {
  id: string;
  agentId: string | null;
  agentSlug: string;
  version: number | null;
  source: 'runtime' | 'playground';
  status: 'ok' | 'error';
  error: string | null;
  startedAt: string;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number | null;
  subject: Record<string, unknown>;
  project: string | null;
};

export type SpanRow = {
  id: string;
  parentId: string | null;
  type: 'agent' | 'llm' | 'tool' | 'handoff' | 'guardrail' | 'custom' | 'step';
  name: string;
  provider: string | null;
  model: string | null;
  startedAt: string | null;
  endedAt: string | null;
  latencyMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cachedTokens: number | null;
  costUsd: number | null;
  status: 'ok' | 'error';
  error: string | null;
  input: unknown;
  output: unknown;
  attributes: Record<string, unknown> | null;
};

export type TraceDetail = {
  trace: TraceRow & { input: unknown; output: unknown; endedAt: string; cachedTokens: number; versionId: string | null; runtime: Record<string, unknown> | null };
  spans: SpanRow[];
};

export type Me = { email: string; role: 'admin' | 'editor' | 'viewer'; workspace: { id: string; slug: string; name: string }; region: string };

export type ChatMessage = { role: 'user' | 'assistant' | 'system'; content: string };

export type Snippet = {
  id: string;
  slug: string;
  name: string;
  description: string;
  content: string;
  publishedContent: string | null;
  version: number;
  updatedBy: string | null;
  updatedAt: string;
  publishedAt: string | null;
  publishedBy: string | null;
  usedBy: Array<{ id: string; slug: string; name: string; live: boolean; draft: boolean }>;
};

export type FlowPosition = { x: number; y: number };
export type FlowNode =
  | { id: string; type: 'input'; position: FlowPosition; label?: string }
  | { id: string; type: 'agent'; position: FlowPosition; label?: string; agentSlug: string; inputMap: Record<string, string>; message?: string }
  | { id: string; type: 'condition'; position: FlowPosition; label?: string; when: string; op: 'truthy' | 'equals' | 'notEquals' | 'gt' | 'lt' | 'contains'; value?: string }
  | { id: string; type: 'output'; position: FlowPosition; label?: string; outputMap: Record<string, string> };
export type FlowEdge = { id: string; source: string; target: string; branch?: 'true' | 'false' };
export type FlowConfig = { kind: 'flow'; nodes: FlowNode[]; edges: FlowEdge[]; variables?: Array<{ name: string; required?: boolean; description?: string }>; logPayloads?: boolean };
export const isFlow = (c: unknown): c is FlowConfig => Boolean(c && typeof c === 'object' && (c as FlowConfig).kind === 'flow');

export type AlertRule = {
  id: string;
  agentId: string | null;
  name: string;
  metric: 'error_rate' | 'daily_cost' | 'p95_latency';
  threshold: number;
  windowMinutes: number;
  minRequests: number;
  channel: 'slack' | 'webhook';
  target: string;
  enabled: boolean;
  state: 'ok' | 'firing';
  lastValue: number | null;
  lastEvaluatedAt: string | null;
};
export type AlertEvent = { id: string; ruleId: string; rule: string | null; kind: 'firing' | 'resolved' | 'test'; value: number | null; message: string; delivered: boolean; error: string | null; at: string };

export type TestCase = { id: string; agentId: string; name: string; input: { variables?: Record<string, unknown>; messages?: ChatMessage[]; input?: string }; baselineOutput: unknown; sourceTraceId: string | null; createdBy: string | null; createdAt: string };
export type TestRun = { id: string; versionId: string; version: number; status: 'running' | 'done' | 'failed'; total: number; passed: number; changed: number; failed: number; createdBy: string | null; startedAt: string; finishedAt: string | null };
export type TestResult = { caseId: string; name: string | null; status: 'same' | 'changed' | 'error'; output: unknown; baseline: unknown; error: string | null; traceId: string | null; durationMs: number | null; inputTokens: number | null; outputTokens: number | null; costUsd: number | null };
